import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Availability } from '../../../types';
import { useIsDarkMode } from '../../../hooks/useDarkMode';
import { getHeatColor } from '../../../utils/heatmap';
import { formatMonthShort, getMondayWeekdayLabels } from '../../../utils/locale';
import { isDayPast, isMonthFullyPast } from '../../../utils/slotTime';
import { PastSlotOverlay } from '../PastSlotOverlay';
const EMPTY_AVAIL: Availability[] = [];

function formatDayKey(y: number, m: number, day: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function buildDayCounts(availability: Availability[]): Map<string, number> {
  const counts = new Map<string, number>();
  const seen = new Map<string, Set<string>>();
  for (const a of availability) {
    if (a?.available && typeof a.day === 'string') {
      let s = seen.get(a.day);
      if (!s) {
        s = new Set();
        seen.set(a.day, s);
      }
      s.add(a.participant);
    }
  }
  seen.forEach((set, day) => counts.set(day, set.size));
  return counts;
}

function todayYmd(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

interface YearViewProps {
  viewDate: Date;
  availability: Availability[];
  onSelectMonth: (year: number, monthIndex: number) => void;
}

export function YearView({ viewDate, availability, onSelectMonth }: YearViewProps) {
  const { i18n } = useTranslation();
  const dayCounts = useMemo(
    () => buildDayCounts(availability ?? EMPTY_AVAIL),
    [availability]
  );
  const maxCount = useMemo(() => {
    let m = 0;
    dayCounts.forEach(v => {
      if (v > m) m = v;
    });
    return m;
  }, [dayCounts]);
  const dark = useIsDarkMode();
  const y = Number.isFinite(viewDate.getTime()) ? viewDate.getFullYear() : new Date().getFullYear();
  const tKey = todayYmd();
  const weekdayNarrow = useMemo(
    () => getMondayWeekdayLabels(i18n.language, 'narrow'),
    [i18n.language]
  );

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
      {Array.from({ length: 12 }, (_, m) => {
        const first = new Date(y, m, 1);
        let sd = first.getDay();
        sd = sd === 0 ? 6 : sd - 1;
        const dim = new Date(y, m + 1, 0).getDate();
        const label = formatMonthShort(first, i18n.language);
        const monthFullyPast = isMonthFullyPast(first);

        return (
          <button
            key={m}
            type="button"
            data-zoom-month={`${y}-${String(m + 1).padStart(2, '0')}`}
            onClick={() => onSelectMonth(y, m)}
            className={`relative cursor-pointer rounded-xl border border-room-border p-2 text-left transition-colors hover:border-room-border-strong hover:bg-room-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent sm:p-2.5 ${
              monthFullyPast ? 'opacity-80' : ''
            }`}
          >
            <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-room-text-dim">
              {label}
            </div>
            <span
              className="grid"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '1px' }}
            >
              {weekdayNarrow.map((d, i) => (
                <span
                  key={`dow-${i}`}
                  className="block pb-0.5 text-center text-[7px] text-room-text-faint"
                >
                  {d}
                </span>
              ))}
              {Array.from({ length: sd }, (_, i) => (
                <span key={`pad-${m}-${i}`} className="block p-0.5 text-[7px]" />
              ))}
              {Array.from({ length: dim }, (_, i) => {
                const day = i + 1;
                const dk = formatDayKey(y, m, day);
                const count = dayCounts.get(dk) ?? 0;
                const isT = dk === tKey;
                const isPastDay = isDayPast(dk);
                const heat = count > 0 ? getHeatColor(count, Math.max(maxCount, 1), dark) : 'transparent';
                return (
                  <span
                    key={`${m}-${day}`}
                    data-zoom-date={dk}
                    className={`relative block rounded-sm py-0.5 text-center text-[7.5px] leading-none ${
                      isT
                        ? 'font-medium'
                        : count > 0
                          ? 'font-semibold text-room-text'
                          : 'text-room-text-faint'
                    }`}
                  >
                    <span
                      className={`inline-flex min-w-[14px] items-center justify-center rounded-full px-0.5 ${
                        isT ? 'today-date-mark' : ''
                      }`}
                      style={{
                        backgroundColor: !isT && count > 0 ? heat : undefined,
                      }}
                    >
                      {day}
                    </span>
                    {isPastDay ? <PastSlotOverlay fine rounded="rounded-sm" /> : null}
                  </span>
                );
              })}
            </span>
          </button>
        );
      })}
    </div>
  );
}
